import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type PointerEvent,
} from 'react';
import { createPortal } from 'react-dom';
import type { AnchorRect } from '../features/day/dayUiStore';
import styles from './Tooltip.module.css';

/** Hovering or keyboard-focusing an element with `data-tip` shows its text after this long. */
export const TOOLTIP_DELAY_MS = 120;

/** An element can ask for its own delay with `data-tip-delay` (learning tips use 250ms, §3.4.3). */
function delayOf(element: HTMLElement): number {
  const raw = Number(element.dataset['tipDelay']);
  return Number.isFinite(raw) && raw > 0 ? raw : TOOLTIP_DELAY_MS;
}

const GAP_PX = 6;
const EDGE_PX = 8;

export interface TooltipTarget {
  /** The element carrying `data-tip`. */
  readonly element: HTMLElement;
  readonly text: string;
  readonly anchor: AnchorRect;
}

export interface TooltipHandlers {
  readonly onPointerOver: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerOut: (event: PointerEvent<HTMLElement>) => void;
  readonly onPointerDown: () => void;
  readonly onFocus: (event: FocusEvent<HTMLElement>) => void;
  readonly onBlur: () => void;
}

export interface TooltipControl {
  /** The visible tooltip, or null. */
  readonly target: TooltipTarget | null;
  /** Spread on the container whose `[data-tip]` descendants get tooltips (event delegation). */
  readonly handlers: TooltipHandlers;
  readonly hide: () => void;
}

function tipElementOf(node: EventTarget | null): HTMLElement | null {
  return node instanceof Element ? node.closest<HTMLElement>('[data-tip]') : null;
}

/** Keyboard focus only: a tap or click that focuses an element must not pop a tooltip up. */
function isKeyboardFocus(element: Element): boolean {
  try {
    return element.matches(':focus-visible');
  } catch {
    return true;
  }
}

/**
 * Delegated tooltips for `[data-tip]` descendants: shown 120 ms after the mouse enters or keyboard
 * focus lands on one, hidden when the pointer leaves, a press starts, focus leaves, a scroll
 * happens or Esc is pressed. Touch never shows one (the tap already opens the element's own UI).
 */
export function useTooltip(): TooltipControl {
  const [target, setTarget] = useState<TooltipTarget | null>(null);
  const timer = useRef<number | null>(null);
  const pending = useRef<HTMLElement | null>(null);
  const shown = useRef<HTMLElement | null>(null);

  const cancel = useCallback((): void => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    pending.current = null;
  }, []);

  const hide = useCallback((): void => {
    cancel();
    shown.current = null;
    setTarget(null);
  }, [cancel]);

  const schedule = useCallback(
    (element: HTMLElement): void => {
      if (pending.current === element || shown.current === element) return;
      cancel();
      pending.current = element;
      timer.current = window.setTimeout(() => {
        timer.current = null;
        pending.current = null;
        const text = element.dataset['tip'];
        if (text === undefined || text === '' || !element.isConnected) return;
        const rect = element.getBoundingClientRect();
        shown.current = element;
        setTarget({
          element,
          text,
          anchor: { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom },
        });
      }, delayOf(element));
    },
    [cancel],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') hide();
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', hide, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', hide, true);
      cancel();
    };
  }, [hide, cancel]);

  const handlers: TooltipHandlers = {
    onPointerOver: (event) => {
      if (event.pointerType === 'touch') return;
      const element = tipElementOf(event.target);
      if (element === null) hide();
      else schedule(element);
    },
    onPointerOut: (event) => {
      const element = tipElementOf(event.target);
      if (element === null) return;
      const next = event.relatedTarget;
      if (next instanceof Node && element.contains(next)) return;
      hide();
    },
    onPointerDown: hide,
    onFocus: (event) => {
      const element = tipElementOf(event.target);
      if (element !== null && event.target === element && isKeyboardFocus(element)) schedule(element);
    },
    onBlur: hide,
  };
  return { target, handlers, hide };
}

interface TooltipBubbleProps {
  readonly id: string;
  readonly text: string;
  readonly anchor: AnchorRect;
}

/** The floating text: above the anchor when there is room, else below, kept inside the viewport. */
export function TooltipBubble({ id, text, anchor }: TooltipBubbleProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [placed, setPlaced] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null) return;
    const { width, height } = element.getBoundingClientRect();
    const above = anchor.top - GAP_PX - height;
    const top = above >= EDGE_PX ? above : anchor.bottom + GAP_PX;
    const left = Math.min(
      Math.max(EDGE_PX, anchor.left),
      Math.max(EDGE_PX, window.innerWidth - width - EDGE_PX),
    );
    element.style.left = `${left}px`;
    element.style.top = `${top}px`;
    setPlaced(true);
  }, [anchor, text]);

  return createPortal(
    <div
      ref={ref}
      id={id}
      role="tooltip"
      className={styles.tooltip}
      style={{ visibility: placed ? 'visible' : 'hidden' }}
      data-testid="tooltip"
    >
      {text}
    </div>,
    document.body,
  );
}
